import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Transition, Dialog } from '@headlessui/react';
import {
  XMarkIcon,
  CreditCardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import authService from '../services/auth.service';
import billingService from '../services/billing.service';
import { User, ProductLine } from '../types/auth.types';
import {
  BillingSummary,
  TiersResponse,
  BillingTier,
  SubscriptionStatus,
  TierInfo,
  Invoice,
  ProductTierInfo,
} from '../types/billing.types';
import AuthenticatedLayout from '../components/AuthenticatedLayout';

// Product line badge styling (non-translatable CSS)
const PRODUCT_BADGES: Record<ProductLine, string> = {
  comply: 'bg-blue-100 text-blue-800',
  quality: 'bg-green-100 text-green-800',
  full: 'bg-purple-100 text-purple-800',
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('billing');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [availableTiers, setAvailableTiers] = useState<TierInfo[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState<BillingTier | null>(null);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [isCancelingDowngrade, setIsCancelingDowngrade] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [invoiceHistory, setInvoiceHistory] = useState<Invoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);

  useEffect(() => {
    const initializeBilling = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
        // Fetch product-specific tiers based on user's product line
        await fetchAvailableTiers(userData.product_line as ProductLine);
      } catch (error) {
        navigate('/signin');
      }
    };

    initializeBilling();
    fetchBillingData();
    fetchInvoiceHistory();
  }, []);

  const fetchUserData = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (error) {
      navigate('/signin');
    }
  };

  const fetchBillingData = async () => {
    try {
      const summary = await billingService.getBillingSummary();
      setBillingSummary(summary);
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      showToastMessage(t('billing:messages.loadError'), true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableTiers = async (productLine?: ProductLine) => {
    try {
      if (productLine) {
        // Fetch product-specific tiers
        const tiersResponse = await billingService.getProductTiers(productLine);
        setAvailableTiers(tiersResponse.tiers);
      } else {
        // Fall back to legacy tiers
        const tiersResponse = await billingService.getAvailableTiers();
        setAvailableTiers(tiersResponse.tiers);
      }
    } catch (error) {
      console.error('Failed to fetch available tiers:', error);
    }
  };

  const fetchInvoiceHistory = async () => {
    setIsLoadingInvoices(true);
    try {
      const historyResponse = await billingService.getInvoiceHistory(12);
      setInvoiceHistory(historyResponse.invoices);
    } catch (error) {
      console.error('Failed to fetch invoice history:', error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleManagePaymentMethod = async () => {
    try {
      const returnUrl = window.location.href;
      const response = await billingService.createPortalSession(returnUrl);
      window.location.href = response.portal_url;
    } catch (error) {
      showToastMessage(t('billing:messages.paymentPortalError'), true);
    }
  };

  const getTierOrder = (tier: BillingTier): number => {
    const order: Record<string, number> = {
      [BillingTier.FREE]: 0,
      [BillingTier.STARTER]: 1,
      [BillingTier.GROWTH]: 2,
    };
    return order[tier] ?? 0;
  };

  const isUpgrade = (currentTier: BillingTier, targetTier: BillingTier): boolean => {
    return getTierOrder(targetTier) > getTierOrder(currentTier);
  };

  const handleTierChange = async (tier: BillingTier) => {
    if (!billingSummary) return;

    const currentTier = billingSummary.subscription.tier;

    if (tier === currentTier) {
      showToastMessage(t('billing:messages.alreadyOnTier'), true);
      return;
    }

    try {
      if (isUpgrade(currentTier, tier)) {
        // Handle upgrade
        if (currentTier === BillingTier.FREE || !billingSummary.subscription.stripe_subscription_id) {
          // First time subscribing - use checkout
          const successUrl = `${window.location.origin}/billing?success=true`;
          const cancelUrl = window.location.href;

          const response = await billingService.createCheckoutSession({
            tier,
            success_url: successUrl,
            cancel_url: cancelUrl,
          });

          window.location.href = response.checkout_url;
        } else {
          // Upgrading existing subscription
          await billingService.upgradeSubscription(tier);
          showToastMessage(t('billing:messages.upgradeSuccess'));
          await fetchBillingData();
          await fetchUserData();
        }
      } else {
        // Handle downgrade
        const response = await billingService.downgradeSubscription(tier);
        showToastMessage(response.message || t('billing:messages.downgradeScheduled'));
        await fetchBillingData();
        await fetchUserData();
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || t('billing:messages.changeError');
      showToastMessage(errorMessage, true);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    try {
      await billingService.cancelSubscription(true);
      setShowCancelModal(false);
      showToastMessage(t('billing:messages.cancelSuccess'));
      fetchBillingData();
    } catch (error) {
      showToastMessage(t('billing:messages.cancelError'), true);
    } finally {
      setIsCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setIsReactivating(true);
    try {
      await billingService.reactivateSubscription();
      showToastMessage(t('billing:messages.reactivateSuccess'));
      fetchBillingData();
    } catch (error) {
      showToastMessage(t('billing:messages.reactivateError'), true);
    } finally {
      setIsReactivating(false);
    }
  };

  const handleCancelPendingDowngrade = async () => {
    setIsCancelingDowngrade(true);
    try {
      await billingService.cancelPendingDowngrade();
      showToastMessage(t('billing:messages.downgradeCanceled'));
      fetchBillingData();
    } catch (error) {
      showToastMessage(t('billing:messages.downgradeCancelError'), true);
    } finally {
      setIsCancelingDowngrade(false);
    }
  };

  const showToastMessage = (message: string, isError: boolean = false) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    // Use the current language from i18n for date formatting
    const locale = i18n.language || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const translateFeature = (feature: string): string => {
    // First try pattern matching for dynamic content (to avoid i18n key not found warnings)
    // Pattern: "X minutes/month" or "X,XXX minutes/month"
    const minutesMatch = feature.match(/^([\d,]+)\s+minutes\/month$/i);
    if (minutesMatch) {
      const count = minutesMatch[1];
      const pattern = t('billing:planFeatures.patterns.minutesPerMonth');
      return pattern.replace('{count}', count);
    }

    // Then try exact match for static features
    const exactKey = `billing:planFeatures.exact.${feature}`;
    const exactTranslation = t(exactKey);
    if (exactTranslation !== exactKey) {
      return exactTranslation;
    }

    // No translation found, return original
    return feature;
  };

  const translateStatus = (status: string): string => {
    // Convert status to lowercase for the translation key
    const statusKey = status.toLowerCase();
    const translationKey = `billing:status.${statusKey}`;
    const translated = t(translationKey);
    return translated === translationKey ? status : translated;
  };

  const translateTierName = (name: string): string => {
    // Try to translate tier name, fallback to original
    const tierKey = name.toLowerCase();
    const translationKey = `billing:tierNames.${tierKey}`;
    const translated = t(translationKey);
    return translated === translationKey ? name : translated;
  };

  const getStatusBadgeColor = (status: SubscriptionStatus) => {
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SubscriptionStatus.CANCELED:
        return 'bg-red-100 text-red-800';
      case SubscriptionStatus.TRIALING:
        return 'bg-blue-100 text-blue-800';
      case SubscriptionStatus.PAST_DUE:
      case SubscriptionStatus.UNPAID:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateUsagePercentage = () => {
    if (!billingSummary) return 0;
    const { minutes_used_in_plan, minutes_included } = billingSummary.usage;
    return Math.min((minutes_used_in_plan / minutes_included) * 100, 100);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <AuthenticatedLayout>
      <header>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold leading-tight tracking-tight text-gray-900">
              {t('billing:title')}
            </h1>
          </div>
        </header>

        <main>
          <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              {billingSummary && (
                <>
                  {/* Current Subscription Card */}
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                    <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {t('billing:subscription.title')}
                          </h3>
                          {user?.product_line && (
                            <span className={classNames(
                              PRODUCT_BADGES[user.product_line as ProductLine] || 'bg-gray-100 text-gray-800',
                              'inline-flex rounded-full px-3 py-0.5 text-xs font-semibold'
                            )}>
                              {t(`billing:productLines.${user.product_line}.name`, user.product_line.toUpperCase())}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                          {t('billing:subscription.subtitle')}
                        </p>
                      </div>
                      <button
                        onClick={handleManagePaymentMethod}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <CreditCardIcon className="-ml-1 mr-2 h-5 w-5" />
                        {t('billing:subscription.managePaymentMethod')}
                      </button>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                      <dl className="sm:divide-y sm:divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">{t('billing:subscription.plan')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <div className="flex flex-col">
                              <div className="flex items-center">
                                <span className="text-lg font-semibold">
                                  {translateTierName(billingSummary.subscription.tier_info.name)}
                                </span>
                                <span className="ml-2 text-gray-600">
                                  ${billingSummary.subscription.tier_info.price_monthly}{t('billing:subscription.perMonth')}
                                </span>
                              </div>
                              {parseFloat(billingSummary.subscription.tier_info.overage_rate) > 0 && (
                                <span className="mt-1 text-sm text-gray-500">
                                  {t('billing:subscription.overage')}: ${billingSummary.subscription.tier_info.overage_rate}{t('billing:subscription.perMinute')}
                                </span>
                              )}
                            </div>
                          </dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">{t('billing:subscription.status')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <span
                              className={classNames(
                                getStatusBadgeColor(billingSummary.subscription.status),
                                'inline-flex rounded-full px-2 text-xs font-semibold leading-5'
                              )}
                            >
                              {translateStatus(billingSummary.subscription.status)}
                            </span>
                            {billingSummary.subscription.canceled_at && (
                              <div className="mt-2 text-sm text-red-600">
                                {t('billing:subscription.cancelsOn')} {formatDate(billingSummary.subscription.current_period_end)}
                                <button
                                  onClick={handleReactivateSubscription}
                                  disabled={isReactivating}
                                  className="ml-4 text-indigo-600 hover:text-indigo-500"
                                >
                                  {isReactivating ? t('billing:subscription.reactivating') : t('billing:subscription.reactivate')}
                                </button>
                              </div>
                            )}
                            {billingSummary.subscription.pending_tier && (
                              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-center">
                                  <ArrowDownIcon className="h-5 w-5 text-amber-600 mr-2" />
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-amber-800">
                                      {t('billing:subscription.scheduledDowngrade', {
                                        tier: translateTierName(billingSummary.subscription.pending_tier),
                                        date: formatDate(billingSummary.subscription.current_period_end),
                                      })}
                                    </span>
                                    <p className="text-xs text-amber-700 mt-1">
                                      {t('billing:subscription.scheduledDowngradeDesc')}
                                    </p>
                                  </div>
                                  <button
                                    onClick={handleCancelPendingDowngrade}
                                    className="ml-3 text-sm font-medium text-amber-800 hover:text-amber-900 underline"
                                  >
                                    {t('billing:subscription.cancelDowngrade')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">{t('billing:subscription.billingPeriod')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            {formatDate(billingSummary.subscription.current_period_start)} -{' '}
                            {formatDate(billingSummary.subscription.current_period_end)}
                          </dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                          <dt className="text-sm font-medium text-gray-500">{t('billing:subscription.features')}</dt>
                          <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                            <ul className="space-y-1">
                              {billingSummary.subscription.tier_info.features.map((feature, idx) => (
                                <li key={idx} className="flex items-center">
                                  <CheckIcon className="h-4 w-4 text-green-500 mr-2" />
                                  {translateFeature(feature)}
                                </li>
                              ))}
                            </ul>
                          </dd>
                        </div>
                      </dl>
                    </div>
                    {billingSummary.subscription.tier !== BillingTier.FREE &&
                      !billingSummary.subscription.canceled_at && (
                        <div className="px-4 py-4 sm:px-6 bg-gray-50 border-t border-gray-200">
                          <button
                            onClick={() => setShowCancelModal(true)}
                            className="text-sm text-red-600 hover:text-red-500"
                          >
                            {t('billing:subscription.cancelSubscription')}
                          </button>
                        </div>
                      )}
                  </div>

                  {/* Usage Statistics Card */}
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8">
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {t('billing:usage.title')}
                      </h3>
                      <p className="mt-1 max-w-2xl text-sm text-gray-500">
                        {t('billing:usage.subtitle')}
                      </p>
                    </div>
                    <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                      {/* Minutes Usage Progress Bar */}
                      <div className="mb-6">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {t('billing:usage.planMinutesUsed')}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            {billingSummary.usage.minutes_used_in_plan} / {billingSummary.usage.minutes_included}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            className={classNames(
                              calculateUsagePercentage() >= 100
                                ? 'bg-red-600'
                                : calculateUsagePercentage() >= 80
                                ? 'bg-yellow-400'
                                : 'bg-green-600',
                              'h-2.5 rounded-full transition-all duration-300'
                            )}
                            style={{ width: `${calculateUsagePercentage()}%` }}
                          ></div>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          {billingSummary.usage.minutes_remaining > 0
                            ? `${billingSummary.usage.minutes_remaining} ${t('billing:usage.minutesRemaining')}`
                            : t('billing:usage.planExhausted')}
                        </p>
                      </div>

                      {/* Usage Stats Grid */}
                      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="px-4 py-5 bg-gray-50 rounded-lg">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {t('billing:usage.planMinutesUsed')}
                          </dt>
                          <dd className="mt-1 text-3xl font-semibold text-gray-900">
                            {billingSummary.usage.minutes_used_in_plan}
                          </dd>
                          <dd className="mt-1 text-xs text-gray-500">
                            {t('billing:usage.of')} {billingSummary.usage.minutes_included} {t('billing:usage.included')}
                          </dd>
                        </div>
                        <div className="px-4 py-5 bg-gray-50 rounded-lg">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {t('billing:usage.overageMinutes')}
                          </dt>
                          <dd className={classNames(
                            "mt-1 text-3xl font-semibold",
                            billingSummary.usage.overage_minutes > 0 ? "text-orange-600" : "text-gray-900"
                          )}>
                            {billingSummary.usage.overage_minutes}
                          </dd>
                          <dd className="mt-1 text-xs text-gray-500">
                            {t('billing:usage.beyondLimit')}
                          </dd>
                        </div>
                        <div className="px-4 py-5 bg-gray-50 rounded-lg">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {t('billing:usage.totalMinutesUsed')}
                          </dt>
                          <dd className="mt-1 text-3xl font-semibold text-gray-900">
                            {billingSummary.usage.minutes_used}
                          </dd>
                          <dd className="mt-1 text-xs text-gray-500">
                            {t('billing:usage.thisPeriod')}
                          </dd>
                        </div>
                        <div className="px-4 py-5 bg-gray-50 rounded-lg">
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {t('billing:usage.totalAnalyses')}
                          </dt>
                          <dd className="mt-1 text-3xl font-semibold text-gray-900">
                            {billingSummary.usage.total_analyses}
                          </dd>
                          <dd className="mt-1 text-xs text-gray-500">
                            {billingSummary.usage.analysis_count} {t('billing:usage.analyzed')}, {billingSummary.usage.transcription_only_count} {t('billing:usage.transcribed')}
                          </dd>
                        </div>
                      </dl>

                      {/* Overage Cost - Only show if there's overage */}
                      {billingSummary.usage.overage_minutes > 0 && (
                        <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-medium text-orange-900">
                                {t('billing:usage.estimatedOverageCost')}
                              </h4>
                              <p className="mt-1 text-xs text-orange-700">
                                {t('billing:usage.basedOn', { minutes: billingSummary.usage.overage_minutes, rate: billingSummary.subscription.tier_info.overage_rate })}
                              </p>
                            </div>
                            <div className="text-2xl font-bold text-orange-900">
                              ${billingSummary.estimated_overage_cost}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Available Plans */}
                  <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {t('billing:plans.title')}
                      </h3>
                      {user?.product_line && user.product_line !== 'full' && (
                        <div className="text-sm text-gray-600">
                          {t(`billing:productLines.${user.product_line}.upgradePrompt`)}{' '}
                          <a href="/signup?product=full" className="text-indigo-600 hover:text-indigo-500 font-medium">
                            {t('billing:plans.upgradeToFull')}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Plan Change Policy Notice */}
                    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-blue-800">
                            {t('billing:plans.planChangeNotice.title')}
                          </h4>
                          <p className="mt-1 text-sm text-blue-700">
                            {t('billing:plans.planChangeNotice.description')}
                          </p>
                          {billingSummary && billingSummary.usage.overage_minutes > 0 && (
                            <p className="mt-2 text-sm font-medium text-blue-800">
                              {t('billing:plans.planChangeNotice.withOverage', { amount: billingSummary.estimated_overage_cost })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {availableTiers.map((tier) => {
                        const isCurrentTier = tier.tier === billingSummary.subscription.tier;
                        return (
                          <div
                            key={tier.tier}
                            className={classNames(
                              isCurrentTier
                                ? 'border-indigo-500 border-2'
                                : 'border-gray-200 border',
                              'relative rounded-lg bg-white px-6 py-5 shadow-sm hover:border-gray-300'
                            )}
                          >
                            {isCurrentTier && (
                              <div className="absolute top-0 right-0 -mt-2 -mr-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  {t('billing:subscription.currentPlan')}
                                </span>
                              </div>
                            )}
                            <div className="flex flex-col h-full">
                              <h4 className="text-lg font-medium text-gray-900">{translateTierName(tier.name)}</h4>
                              <p className="mt-1 text-3xl font-bold text-gray-900">
                                ${tier.price_monthly}
                                <span className="text-base font-normal text-gray-500">{t('billing:subscription.perMonth')}</span>
                              </p>
                              {parseFloat(tier.overage_rate) > 0 && (
                                <p className="mt-1 text-sm text-gray-600">
                                  {t('billing:plans.overageRate', { rate: tier.overage_rate })}
                                </p>
                              )}
                              <ul className="mt-4 space-y-2 flex-1">
                                {tier.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                                    <span className="text-sm text-gray-700">{translateFeature(feature)}</span>
                                  </li>
                                ))}
                              </ul>
                              {!isCurrentTier && (
                                <button
                                  onClick={() => handleTierChange(tier.tier)}
                                  className="mt-4 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                  {billingSummary && isUpgrade(billingSummary.subscription.tier, tier.tier) ? t('billing:plans.upgrade') : t('billing:plans.downgrade')}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Billing History */}
                  <div className="mb-8">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      {t('billing:history.title')}
                    </h3>
                    {isLoadingInvoices ? (
                      <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : invoiceHistory.length === 0 ? (
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <div className="px-4 py-5 sm:p-6 text-center text-gray-500">
                          {t('billing:history.noHistory')}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing:history.date')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing:history.description')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing:history.status')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing:history.amount')}
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('billing:history.invoice')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {invoiceHistory.map((invoice) => (
                              <tr key={invoice.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(invoice.created * 1000).toLocaleDateString(i18n.language || 'en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  <div>
                                    {invoice.description}
                                    {invoice.invoice_number && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        #{invoice.invoice_number}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={classNames(
                                    invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                                    invoice.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800',
                                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
                                  )}>
                                    {translateStatus(invoice.status)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  ${invoice.total.toFixed(2)} {invoice.currency}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {invoice.invoice_pdf ? (
                                    <a
                                      href={invoice.invoice_pdf}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      {t('billing:history.downloadPdf')}
                                    </a>
                                  ) : invoice.hosted_invoice_url ? (
                                    <a
                                      href={invoice.hosted_invoice_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-indigo-600 hover:text-indigo-900"
                                    >
                                      {t('billing:history.viewInvoice')}
                                    </a>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </main>

      {/* Cancel Subscription Modal */}
      <Transition.Root show={showCancelModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setShowCancelModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        {t('billing:cancelModal.title')}
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {t('billing:cancelModal.message')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={handleCancelSubscription}
                      disabled={isCanceling}
                    >
                      {isCanceling ? t('billing:cancelModal.canceling') : t('billing:cancelModal.confirmButton')}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => setShowCancelModal(false)}
                      disabled={isCanceling}
                    >
                      {t('billing:cancelModal.keepButton')}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Toast Notification */}
      <div
        aria-live="assertive"
        className="fixed inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
      >
        <div className="w-full flex flex-col items-center space-y-4 sm:items-end">
          <Transition
            show={showToast}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900">{toastMessage}</p>
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      onClick={() => setShowToast(false)}
                    >
                      <span className="sr-only">{t('billing:subscription.close')}</span>
                      <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default Billing;
